import { useEffect, useState } from 'react'
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { authApi, setProjectReason, projectAreaApi, type Project } from '../../api/client'
import { useMe } from '../../components/AuthGate'
import { apiError } from '../../components/ProvisionJobLog'
import { Chip } from '../../components/ui/Chip'
import { roleLabel, scopedRoleLabel, useCan } from '../../lib/auth'

export interface ProjectContext {
  project: Project
  refetch: () => void
}

export function useProject() {
  return useOutletContext<ProjectContext>()
}

const reasonKey = (id: string) => `xc.reason.${id}`

// ProjectLayout: the project area. It shows only what the project owns
// (servers, networks, members), in the project view of the API. Platform staff
// see it as the customer does; changes in a customer's project need a reason,
// system projects are the staff's own.
export function ProjectLayout() {
  const { project: id = '' } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const can = useCan()
  const [navOpen, setNavOpen] = useState(false)
  useEffect(() => setNavOpen(false), [location.pathname])
  const { data: project, error, isLoading, refetch } = useQuery({ queryKey: ['project-area', id, 'project'], queryFn: () => projectAreaApi.project(id), retry: false })
  const { data: list } = useQuery({ queryKey: ['project-area', 'projects'], queryFn: projectAreaApi.projects })
  const readReason = (pid: string) => {
    try { return sessionStorage.getItem(reasonKey(pid)) || '' } catch { return '' }
  }
  const [reason, setReason] = useState(() => readReason(id))
  // another project chosen: its own reason, before the effect below stores
  // the old one under the new project's key
  const [reasonOf, setReasonOf] = useState(id)
  if (reasonOf !== id) {
    setReasonOf(id)
    setReason(readReason(id))
  }
  useEffect(() => {
    setProjectReason(reason)
    try { if (reason) sessionStorage.setItem(reasonKey(id), reason); else sessionStorage.removeItem(reasonKey(id)) } catch { /* private mode */ }
    return () => setProjectReason('')
  }, [reason, id])

  if (isLoading) return <div className="p-8 text-muted-foreground">Loading…</div>
  if (!project) {
    return (
      <div className="mx-auto max-w-xl p-8">
        <h1 className="text-xl font-semibold">Project not available</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error ? apiError(error) : 'This project does not exist or you have no role in it.'}</p>
        <Link className="mt-3 inline-block text-sm underline" to="/p">Your projects</Link>
      </div>
    )
  }
  const base = `/p/${project.id}`
  const nav = [
    { to: base, label: 'Overview', end: true },
    { to: `${base}/servers`, label: 'Servers' },
    { to: `${base}/networks`, label: 'Networks & IPs' },
    { to: `${base}/volumes`, label: 'Volumes' },
    { to: `${base}/activity`, label: 'Activity' },
    { to: `${base}/access`, label: 'Members & access' },
    ...(project.can_admin ? [{ to: `${base}/settings`, label: 'Settings' }] : []),
  ]
  const others = (list || []).filter((p) => p.id !== project.id)

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-20 flex flex-wrap items-center gap-2 border-b bg-card px-4 py-2.5 md:px-7">
        <button className="rounded-md border px-2 py-1 text-sm md:hidden" onClick={() => setNavOpen(!navOpen)} aria-label="Menu" aria-expanded={navOpen}>☰</button>
        <Link to={can.staff ? '/ops/overview' : '/p'} className="font-display text-xl font-bold tracking-tight text-primary">xControl</Link>
        {can.staff && <Link to={`/ops/projects/${project.id}`} title="Back to the operator area" className="rounded-[3px] bg-lime px-1.5 py-0.5 text-[11px] font-extrabold uppercase tracking-wider text-ink">Ops</Link>}
        {can.staff && <span className="text-muted-foreground" aria-hidden>›</span>}
        <Chip tone="neutral" className="font-mono" title={project.customer_name}>{project.customer_code}</Chip>
        {project.system && <Chip tone="level" title="Runs the platform's own services">System project</Chip>}
        <span className="hidden text-sm text-muted-foreground sm:inline">{project.customer_name}</span>
        <span className="text-muted-foreground" aria-hidden>›</span>
        <select value={project.id} onChange={(e) => navigate(`/p/${e.target.value}`)} aria-label="Project"
          className="h-9 rounded-md border border-sky bg-secondary px-2 text-sm font-extrabold text-primary">
          <option value={project.id}>{project.name}</option>
          {others.map((p) => <option key={p.id} value={p.id}>{p.customer_code} · {p.name}</option>)}
        </select>
        <div className="flex-1" />
        {project.role === 'project-reader' && <Chip tone="outline">Read only</Chip>}
        <ProjectUserMenu project={project} />
      </header>
      {project.staff && project.system && (
        <div className="flex flex-wrap items-center gap-2 border-b border-[#C9DDEC] bg-secondary px-4 py-2 text-sm text-primary md:px-7">
          <span><b className="text-foreground">{project.name}</b> is a system project: it runs the platform's own services. {project.role !== 'project-reader' ? 'You work in it without a reason; changes are logged under your name.' : 'You can read it.'}</span>
          <Link className="underline" to={`/ops/projects/${project.id}`}>Back to operations</Link>
        </div>
      )}
      {project.staff && !project.system && (
        <div className="flex flex-wrap items-center gap-2 border-b border-[#F0D49A] bg-[#FFF1D6] px-4 py-2 text-sm text-[#5E3500] md:px-7">
          <span>You see <b>{project.name}</b> as the customer does ({roleLabel(can.role)}).{project.needs_reason && ' Changes need a reason and are logged under your name.'}</span>
          {project.needs_reason && (
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason, e.g. ticket #1234" aria-label="Reason for changes"
              className="min-w-[14rem] flex-1 rounded border border-[#F0D49A] bg-white px-2 py-1 text-sm text-foreground dark:bg-background sm:max-w-sm" />
          )}
          <Link className="underline" to={`/ops/projects/${project.id}`}>Back to operations</Link>
        </div>
      )}
      <div className="flex">
        <aside className={`${navOpen ? 'block' : 'hidden'} fixed inset-y-0 left-0 top-[57px] z-10 w-56 shrink-0 overflow-y-auto border-r bg-card md:sticky md:block md:h-[calc(100vh-57px)]`}>
          <nav className="py-3 text-sm" aria-label="Project">
            <div className="px-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Project</div>
            {nav.map((i) => (
              <NavLink key={i.to} to={i.to} end={i.end}
                className={({ isActive }) => `mx-2 block rounded-md px-3 py-2 font-bold ${isActive ? 'bg-secondary text-primary shadow-[inset_3px_0_0_hsl(var(--primary))]' : 'text-foreground/80 hover:bg-secondary'}`}>
                {i.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="min-w-0 flex-1 px-4 py-5 md:px-8">
          {!project.active && <div className="mb-4 rounded-md border bg-muted px-3 py-2 text-sm">This project is archived: its resources stay, new servers cannot be created.</div>}
          <Outlet context={{ project, refetch } satisfies ProjectContext} />
        </main>
      </div>
    </div>
  )
}

function ProjectUserMenu({ project }: { project: Project }) {
  const me = useMe()
  if (!me) return null
  const logout = async () => {
    try { await authApi.logout() } finally { window.location.assign('/') }
  }
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="hidden text-right leading-tight sm:block">
        <span className="block">{me.username}</span>
        <span className="block text-xs text-muted-foreground">{project.staff ? `${roleLabel(me.role)} · viewing` : scopedRoleLabel(project.role)}</span>
      </span>
      <button onClick={logout} className="rounded-md border px-2 py-1 text-xs hover:bg-muted">Log out</button>
    </div>
  )
}

// ProjectPicker: the projects a person may open, grouped by customer.
export function ProjectPicker() {
  const me = useMe()
  const can = useCan()
  const navigate = useNavigate()
  const { data, isLoading } = useQuery({ queryKey: ['project-area', 'projects'], queryFn: projectAreaApi.projects })
  const [creating, setCreating] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [error, setError] = useState('')
  if (isLoading) return <div className="p-8 text-muted-foreground">Loading…</div>
  const projects = data || []
  if (!can.staff && projects.length === 1) return <Navigate to={`/p/${projects[0].id}`} replace />
  const customerAdminOf = new Set((me?.bindings || []).filter((b) => b.role === 'customer-admin').map((b) => b.scope_id))
  const groups = Array.from(new Set([...projects.map((p) => p.customer_id), ...customerAdminOf]))
  const create = async (cid: string) => {
    setError('')
    try {
      const p = await projectAreaApi.createProject(cid, { name })
      navigate(`/p/${p.id}`)
    } catch (e) {
      setError(apiError(e))
    }
  }
  return (
    <div className="min-h-screen bg-background px-4 py-10 text-foreground">
      <div className="mx-auto max-w-2xl">
        <div className="mb-6 flex items-center justify-between">
          <span className="text-lg font-semibold">xControl</span>
          {can.staff ? <Link className="text-sm underline" to="/ops/overview">Operator area</Link> : <span className="text-sm text-muted-foreground">{me?.username}</span>}
        </div>
        <h1 className="text-2xl font-semibold">Your projects</h1>
        {groups.length === 0 && <p className="mt-3 text-sm text-muted-foreground">You have no project yet. Ask your project manager or the platform team for access.</p>}
        {error && <div className="mt-3 rounded bg-[#FCE4E1] p-2 text-sm text-[#A3271D]">{error}</div>}
        <div className="mt-4 space-y-4">
          {groups.map((cid) => {
            const ps = projects.filter((p) => p.customer_id === cid)
            const first = ps[0]
            return (
              <div key={cid} className="rounded-lg border bg-card">
                <div className="flex items-center justify-between border-b px-4 py-2">
                  <span className="font-medium">{first ? <><Chip tone="warn" className="font-mono">{first.customer_code}</Chip> {first.customer_name}</> : cid}</span>
                  {customerAdminOf.has(cid) && <button className="text-xs underline" onClick={() => { setCreating(cid); setName('') }}>New project</button>}
                </div>
                {creating === cid && (
                  <div className="flex flex-wrap gap-2 border-b px-4 py-2">
                    <input className="rounded-md border bg-background px-3 py-1.5 text-sm" value={name} onChange={(e) => setName(e.target.value)} placeholder="project name, e.g. web-prod" aria-label="Project name" />
                    <button className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50" disabled={!name.trim()} onClick={() => create(cid)}>Create</button>
                    <span className="self-center text-xs text-muted-foreground">starts with small quotas; the platform team raises them</span>
                  </div>
                )}
                <ul className="divide-y">
                  {ps.map((p) => (
                    <li key={p.id}>
                      <Link to={`/p/${p.id}`} className="flex items-center justify-between px-4 py-2.5 hover:bg-muted">
                        <span className="font-medium">{p.name}{p.system && <span className="ml-2 text-xs text-muted-foreground">system project</span>}{!p.active && <span className="ml-2 text-xs text-muted-foreground">archived</span>}</span>
                        <span className="text-xs text-muted-foreground">{scopedRoleLabel(p.role)}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
